import { ThemeEvents } from '@theme/events';

/**
 * Color preview picker — switches product media gallery without changing cart variant.
 * Pack option changes only jump to the variant featured image; all slides stay visible.
 */
class ColorPreviewPicker extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #sectionController;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);

    this.#sectionController?.abort();
    this.#sectionController = new AbortController();
    const { signal } = this.#sectionController;
    const section = this.closest('.shopify-section, dialog');

    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#sectionController?.abort();
  }

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    this.#showAllSlides();
    this.#clearColorSelection();

    const featuredMediaId = event.detail?.resource?.featured_media?.id;
    if (featuredMediaId) {
      this.#goToFeaturedMedia(featuredMediaId);
    }
  };

  /**
   * @returns {Element | null}
   */
  #getGallery() {
    return (
      this.closest('media-gallery') ??
      this.closest('.product-information__media')?.querySelector('media-gallery') ??
      document.querySelector('.product-information media-gallery')
    );
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getSlideshow() {
    const gallery = this.#getGallery();
    const slideshow = gallery?.querySelector('slideshow-component');
    return slideshow instanceof HTMLElement ? slideshow : null;
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getAllSlides() {
    const gallery = this.#getGallery();
    if (!gallery) return [];

    return Array.from(gallery.querySelectorAll('slideshow-slide')).filter(
      (slide) => slide instanceof HTMLElement
    );
  }

  /**
   * @param {HTMLElement} slide
   * @returns {string}
   */
  #getSlideAlt(slide) {
    const img = slide.querySelector('.product-media__image, img');
    return (img?.getAttribute('alt') || '').toLowerCase();
  }

  /**
   * @param {HTMLElement} slide
   * @returns {boolean}
   */
  #isGenericSlide(slide) {
    if (slide.dataset.colorKey) return false;

    const alt = this.#getSlideAlt(slide);
    const knownColors = ['cobalt blue', 'teal green', 'graphite black', 'mist pink'];
    return !knownColors.some((color) => alt.includes(color));
  }

  /**
   * @param {HTMLElement} slide
   * @param {string} colorName
   * @returns {boolean}
   */
  #slideMatchesColor(slide, colorName) {
    const colorLower = colorName.toLowerCase();
    const slideColor = (slide.dataset.colorKey || '').toLowerCase();
    const alt = this.#getSlideAlt(slide);

    return slideColor === colorLower || alt.includes(colorLower);
  }

  /**
   * @param {HTMLElement[]} slides
   * @param {HTMLElement | undefined} targetSlide
   */
  #selectSlide(slides, targetSlide) {
    const slideshow = this.#getSlideshow();
    if (!slideshow || !targetSlide || typeof slideshow.select !== 'function') return;

    const slideId = targetSlide.getAttribute('slide-id');
    if (slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    const visibleSlides = slideshow.slides?.length
      ? slideshow.slides
      : slides.filter((slide) => !slide.hasAttribute('hidden'));
    const index = visibleSlides.indexOf(targetSlide);

    if (index >= 0) {
      slideshow.select(index, undefined, { animate: false });
    }
  }

  #showAllSlides() {
    const gallery = this.#getGallery();
    if (!gallery) return;

    gallery.querySelectorAll('slideshow-slide').forEach((slide) => {
      slide.removeAttribute('hidden');
      slide.setAttribute('aria-hidden', 'false');
      slide.removeAttribute('reveal');
    });
  }

  /**
   * @param {string | number} featuredMediaId
   */
  #goToFeaturedMedia(featuredMediaId) {
    const slides = this.#getAllSlides();
    const featuredSlide = slides.find((slide) => slide.getAttribute('slide-id') === String(featuredMediaId));
    if (featuredSlide) {
      this.#selectSlide(slides, featuredSlide);
    }
  }

  #clearColorSelection() {
    this.querySelectorAll('[data-preview-color]').forEach((button) => {
      button.classList.remove('is-active');
      button.setAttribute('aria-pressed', 'false');
    });
  }

  /**
   * @param {Event} event
   */
  #handleClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-preview-color]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const colorName = button.dataset.previewColor;
    if (!colorName) return;

    this.#setActive(button);

    const variantId = button.dataset.previewVariantId;
    const productUrl = this.dataset.productUrl;

    if (variantId && productUrl) {
      const updated = await this.#updateGallery(productUrl, variantId);
      if (updated) return;
    }

    this.#showColorSlides(colorName);
  };

  /**
   * @param {HTMLButtonElement} activeButton
   */
  #setActive(activeButton) {
    this.querySelectorAll('[data-preview-color]').forEach((button) => {
      button.classList.toggle('is-active', button === activeButton);
      button.setAttribute('aria-pressed', button === activeButton ? 'true' : 'false');
    });
  }

  /**
   * @param {string} colorName
   */
  #showColorSlides(colorName) {
    const slides = this.#getAllSlides();
    const slideshow = this.#getSlideshow();
    if (!slideshow) return;

    const matchingSlides = slides.filter((slide) => this.#slideMatchesColor(slide, colorName));

    if (!matchingSlides.length) return;

    slides.forEach((slide) => {
      const showSlide = matchingSlides.includes(slide) || this.#isGenericSlide(slide);
      slide.toggleAttribute('hidden', !showSlide);
      slide.setAttribute('aria-hidden', showSlide ? 'false' : 'true');
      slide.removeAttribute('reveal');
    });

    this.#selectSlide(slides, matchingSlides[0]);
  }

  /**
   * @param {Element} currentGallery
   * @param {Element} newGallery
   */
  #replaceGalleryMedia(currentGallery, newGallery) {
    const replaceableSelectors = ['slideshow-component', 'zoom-dialog', '.media-gallery__grid'];

    for (const selector of replaceableSelectors) {
      const newElement = newGallery.querySelector(selector);
      const currentElement = currentGallery.querySelector(selector);

      if (newElement && currentElement) {
        currentElement.replaceWith(newElement);
      }
    }
  }

  /**
   * @param {string} productUrl
   * @param {string} variantId
   * @returns {Promise<boolean>}
   */
  async #updateGallery(productUrl, variantId) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) return false;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGallery = doc.querySelector('media-gallery');
      const currentGallery = this.#getGallery();

      if (newGallery && currentGallery) {
        this.#replaceGalleryMedia(currentGallery, newGallery);
        const activeColor = this.querySelector('[data-preview-color].is-active');
        if (activeColor instanceof HTMLElement && activeColor.dataset.previewColor) {
          this.#showColorSlides(activeColor.dataset.previewColor);
        }
        return true;
      }

      const sectionGallery = doc.querySelector('.product-information media-gallery');
      if (sectionGallery && currentGallery) {
        this.#replaceGalleryMedia(currentGallery, sectionGallery);
        const activeColor = this.querySelector('[data-preview-color].is-active');
        if (activeColor instanceof HTMLElement && activeColor.dataset.previewColor) {
          this.#showColorSlides(activeColor.dataset.previewColor);
        }
        return true;
      }

      return false;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return false;
      console.error(error);
      return false;
    }
  }
}

if (!customElements.get('color-preview-picker')) {
  customElements.define('color-preview-picker', ColorPreviewPicker);
}
